import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Download, ArrowRight, Search, X } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { FAMILIES } from "@/lib/families";

/* Manufacturer catalogues — official PDFs, hosted locally under /files. */
const BRAND_CATALOGUES = [
    {
        brand: "KEI",
        title: "KEI Complete Range Catalogue",
        desc: "House wires, LT power & control, HT up to 33 kV, EHV, fire survival, instrumentation, solar and specialty cables.",
        file: "/files/kei-complete-range-catalogue.pdf",
        size: "10.7 MB",
        tag: "Complete range",
    },
    {
        brand: "Polycab",
        title: "Polycab LT Cable Catalogue",
        desc: "LT power and control cables — copper & aluminium, PVC/XLPE, armoured and unarmoured constructions with full technical tables.",
        file: "/files/polycab-lt-cable-catalogue.pdf",
        size: "3.8 MB",
        tag: "LT cables",
    },
    {
        brand: "Havells",
        title: "Havells Industrial Cables Catalogue",
        desc: "Industrial range — LT power & control 1.1 kV, HT up to 33 kV, fire survival and aerial bunched cables.",
        file: "/files/havells-industrial-cables-catalogue.pdf",
        size: "5.3 MB",
        tag: "Industrial",
    },
    {
        brand: "Dowells",
        title: "Dowells Cable Accessories Catalogue",
        desc: "Cable lugs, crimping terminals, brass cable glands, earthing accessories and crimping tools — the full Dowells range.",
        file: "/files/dowells-catalogue.pdf",
        size: "25.8 MB",
        tag: "Lugs & glands",
    },
    {
        brand: "Raychem RPG",
        title: "Raychem RPG Joints & Terminations",
        desc: "Heat-shrink cable joints and terminations from 1.1 kV to EHV, including 33 kV termination kits.",
        file: "/files/raychem-rpg-catalogue.pdf",
        size: "7.2 MB",
        tag: "Jointing kits",
    },
];

const squeeze = (s) => s.toLowerCase().replace(/[^a-z0-9.]+/g, "");
const SLUG_ALIASES = {
    alu: "aluminium", al: "aluminium", cu: "copper", ard: "armoured", arm: "armoured",
    armourd: "armoured", flex: "flexible", swa: "swa",
};
const normQuery = (q) =>
    q.split(/[^a-z0-9.]+/i).filter(Boolean).map((t) => SLUG_ALIASES[t.toLowerCase()] || t.toLowerCase());

export default function Products() {
    const [query, setQuery] = useState("");
    const navigate = useNavigate();

    /* hero search still lands here — surface matching families as quick links */
    useEffect(() => {
        const h = (e) => setQuery(e.detail || "");
        window.addEventListener("roxy:search", h);
        return () => window.removeEventListener("roxy:search", h);
    }, []);

    const matches = useMemo(() => {
        const tokens = normQuery(query);
        if (!tokens.length) return [];
        return FAMILIES.filter((f) =>
            tokens.every((t) =>
                squeeze(`${f.name} ${f.tag} ${f.brands.join(" ")} ${f.standard} ${f.conductor} ${f.insulation} ${f.armour}`).includes(t)
            )
        );
    }, [query]);

    return (
        <section id="products" className="relative hud-bg py-20 lg:py-28" data-testid="products-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-10">
                    <Reveal>
                        <Eyebrow className="mb-5">04 — Brand Catalogues</Eyebrow>
                        <h2 className="font-display font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-ink">
                            Straight from the <em className="text-copper">manufacturers.</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="md:max-w-sm">
                        <p className="text-mist leading-relaxed">
                            Official KEI, Polycab, Havells, Dowells and Raychem RPG catalogues — full constructions,
                            sizes and technical tables. Download, pick your sizes, and get our price in one estimate.
                        </p>
                    </Reveal>
                </div>

                <Reveal>
                    <div className="relative max-w-md mb-4">
                        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-dim" />
                        <input
                            data-testid="product-search-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search our listed families — e.g. 2.5x12C Cu ARD"
                            className="w-full bg-white/70 border border-line rounded-full pl-11 pr-10 py-3 text-sm text-ink placeholder:text-dim focus:outline-none focus:border-copper-bright transition-colors duration-300 backdrop-blur-sm"
                        />
                        {query && (
                            <button
                                data-testid="product-search-clear"
                                onClick={() => setQuery("")}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-dim hover:text-copper transition-colors"
                                aria-label="Clear search"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    {query && (
                        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="product-search-matches">
                            {matches.length === 0 ? (
                                <p className="font-mono text-[11px] text-dim tracking-wider py-2">
                                    No listed family matches — the brand catalogues below cover far more; or send us a BOQ.
                                </p>
                            ) : (
                                <>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">Matching families:</span>
                                    {matches.map((f) => (
                                        <button
                                            key={f.slug}
                                            data-testid={`product-match-${f.slug}`}
                                            onClick={() => navigate(`/family/${f.slug}`)}
                                            className="font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 border border-copper/50 text-copper rounded-full hover:bg-copper hover:text-white transition-colors duration-300"
                                        >
                                            {f.name} <ArrowRight size={11} className="inline ml-1" />
                                        </button>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </Reveal>

                <div className="grid md:grid-cols-3 gap-4 md:gap-5" data-testid="products-grid">
                    {BRAND_CATALOGUES.map((c, i) => (
                        <Reveal key={c.brand} delay={(i % 3) * 0.08}>
                            <div
                                data-testid={`catalogue-card-${c.brand.toLowerCase().replace(/\s+/g, "-")}`}
                                onMouseMove={(e) => {
                                    const el = e.currentTarget;
                                    const r = el.getBoundingClientRect();
                                    const px = (e.clientX - r.left) / r.width - 0.5;
                                    const py = (e.clientY - r.top) / r.height - 0.5;
                                    el.style.transform = `perspective(900px) rotateX(${(-py * 5).toFixed(2)}deg) rotateY(${(px * 7).toFixed(2)}deg) translateY(-3px)`;
                                    el.style.setProperty("--sheen-x", `${(px + 0.5) * 100}%`);
                                }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
                                className="range-card group relative rounded-md border border-line bg-white/80 backdrop-blur-sm p-7 flex flex-col min-h-[320px] transition-transform duration-200 will-change-transform"
                                style={{ transformStyle: "preserve-3d" }}
                            >
                                <span className="range-corners" aria-hidden="true" />
                                <div className="relative z-[3] flex items-start justify-between mb-8">
                                    <span className="font-display font-bold text-4xl tracking-tight text-ink group-hover:text-copper transition-colors duration-300">
                                        {c.brand}
                                    </span>
                                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-copper border border-copper/40 rounded-full px-3 py-1.5">
                                        {c.tag}
                                    </span>
                                </div>
                                <p className="relative z-[3] font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">{c.title}</p>
                                <p className="relative z-[3] text-sm text-mist leading-relaxed mb-8">{c.desc}</p>
                                <div className="relative z-[3] mt-auto flex items-center gap-3">
                                    <a
                                        data-testid={`catalogue-view-${c.brand.toLowerCase().replace(/\s+/g, "-")}`}
                                        href={c.file}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 rounded-full bg-copper text-white font-mono uppercase tracking-[0.15em] text-[10px] py-3 hover:bg-copper-bright transition-colors duration-300 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(29,78,216,0.3)]"
                                    >
                                        <BookOpen size={13} /> View catalogue
                                    </a>
                                    <a
                                        data-testid={`catalogue-download-${c.brand.toLowerCase().replace(/\s+/g, "-")}`}
                                        href={c.file}
                                        download
                                        aria-label={`Download ${c.brand} catalogue PDF`}
                                        className="w-11 h-11 rounded-full border border-line flex items-center justify-center text-mist hover:border-copper hover:text-copper transition-colors duration-300"
                                    >
                                        <Download size={15} />
                                    </a>
                                </div>
                                <p className="relative z-[3] font-mono text-[9px] text-dim tracking-[0.15em] mt-4 uppercase">
                                    Official PDF · {c.size}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.1}>
                    <p className="mt-10 text-center font-mono text-[11px] tracking-[0.15em] text-mist uppercase">
                        Found your size?{" "}
                        <a href="/prices" className="text-copper hover:text-ink transition-colors duration-300" data-testid="catalogue-to-prices">
                            Check our live prices
                        </a>
                        {" · "}
                        <a href="/upload-boq" className="text-copper hover:text-ink transition-colors duration-300" data-testid="catalogue-to-boq">
                            Upload a BOQ
                        </a>
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
